import { Controller } from "@hotwired/stimulus"

// Import Modal Controller - handles file upload and import functionality
// Usage: Add data-controller="import-modal" to the modal content
export default class extends Controller {
  static targets = [
    "dropZone",
    "fileInput",
    "uploadContent",
    "selectedContent",
    "fileName",
    "fileSize",
    "clearBtn",
    "formatDetection",
    "detectedFormat",
    "previewSection",
    "previewContent",
    "previewBtn",
    "submitBtn",
    "progress",
    "progressBar",
    "progressText",
    "validation",
    "validationContent"
  ]

  static values = {
    maxFileSize: Number,
    acceptedFormats: Array
  }

  connect() {
    console.log("Import modal controller connected")
    this.selectedFile = null
  }

  // Trigger file input click when clicking on drop zone
  triggerFileInput(event) {
    event.preventDefault()
    event.stopPropagation()
    console.log("Triggering file input from import modal")

    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  // Handle file selection from input
  handleFileSelect(event) {
    const file = event.target.files[0]
    if (file) {
      console.log("File selected:", file.name)
      this.processFile(file)
    }
  }

  // Handle drag over event
  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("border-blue-400", "bg-blue-100")
    }
  }

  // Handle drag leave event
  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-blue-400", "bg-blue-100")
    }
  }

  // Handle file drop event
  handleFileDrop(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-blue-400", "bg-blue-100")
    }

    const file = event.dataTransfer.files[0]
    if (file) {
      console.log("File dropped:", file.name)
      this.processFile(file)
    }
  }

  // Process the selected file
  processFile(file) {
    // Validate file type
    const fileExt = "." + file.name.split(".").pop().toLowerCase()
    const acceptedFormats = this.acceptedFormatsValue || [".json", ".csv"]

    if (!acceptedFormats.includes(fileExt)) {
      alert(`Invalid file type. Please select: ${acceptedFormats.join(", ")}`)
      return
    }

    // Validate file size
    const maxFileSize = this.maxFileSizeValue || 10 * 1024 * 1024 // Default 10MB
    if (file.size > maxFileSize) {
      alert(`File size exceeds maximum limit of ${(maxFileSize / (1024 * 1024)).toFixed(1)}MB`)
      return
    }

    this.selectedFile = file
    this.updateUI(file)
    this.enableButtons()

    // Auto-preview if enabled
    const autoPreview = this.element.querySelector('input[name="auto_preview"]')
    if (autoPreview && autoPreview.checked) {
      setTimeout(() => this.previewFile(), 100)
    }
  }

  // Update UI with selected file info
  updateUI(file) {
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name
    }

    if (this.hasFileSizeTarget) {
      this.fileSizeTarget.textContent = this.formatFileSize(file.size)
    }

    // Show selected state
    if (this.hasUploadContentTarget) {
      this.uploadContentTarget.classList.add("hidden")
    }

    if (this.hasSelectedContentTarget) {
      this.selectedContentTarget.classList.remove("hidden")
    }

    // Show format detection
    if (this.hasDetectedFormatTarget) {
      const format = file.name.split(".").pop().toLowerCase()
      this.detectedFormatTarget.textContent = format.toUpperCase()
    }

    if (this.hasFormatDetectionTarget) {
      this.formatDetectionTarget.classList.remove("hidden")
    }
  }

  // Format file size for display
  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  // Enable action buttons
  enableButtons() {
    if (this.hasPreviewBtnTarget) {
      this.previewBtnTarget.disabled = false
      this.previewBtnTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }

    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
  }

  // Clear file selection
  clearFile(event) {
    if (event) event.preventDefault()

    this.selectedFile = null

    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = ""
    }

    if (this.hasUploadContentTarget) {
      this.uploadContentTarget.classList.remove("hidden")
    }

    if (this.hasSelectedContentTarget) {
      this.selectedContentTarget.classList.add("hidden")
    }

    if (this.hasFormatDetectionTarget) {
      this.formatDetectionTarget.classList.add("hidden")
    }

    if (this.hasPreviewSectionTarget) {
      this.previewSectionTarget.classList.add("hidden")
    }

    if (this.hasValidationTarget) {
      this.validationTarget.classList.add("hidden")
    }

    if (this.hasPreviewBtnTarget) {
      this.previewBtnTarget.disabled = true
      this.previewBtnTarget.classList.add("opacity-50", "cursor-not-allowed")
    }

    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  // Preview file contents
  async previewFile(event) {
    if (event) event.preventDefault()

    if (!this.selectedFile) return

    try {
      const text = await this.selectedFile.text()
      let displayText = text

      // Format JSON
      if (this.selectedFile.name.toLowerCase().endsWith(".json")) {
        try {
          const parsed = JSON.parse(text)
          displayText = JSON.stringify(parsed, null, 2)
        } catch (e) {
          console.warn("Invalid JSON format")
        }
      }

      // Truncate if too long
      if (displayText.length > 3000) {
        displayText = displayText.substring(0, 3000) + "\n\n... (truncated for preview)"
      }

      if (this.hasPreviewContentTarget) {
        this.previewContentTarget.textContent = displayText
      }

      if (this.hasPreviewSectionTarget) {
        this.previewSectionTarget.classList.remove("hidden")
      }

      // Auto-validate if enabled
      const shouldValidate = this.element.querySelector('input[name="validate_before_import"]')
      if (shouldValidate && shouldValidate.checked) {
        this.validateFile(text)
      }
    } catch (error) {
      alert("Failed to read file: " + error.message)
    }
  }

  // Validate file contents
  validateFile(content) {
    const issues = []

    try {
      if (this.selectedFile.name.toLowerCase().endsWith(".json")) {
        JSON.parse(content)
      }

      if (content.trim().length === 0) {
        issues.push("File is empty")
      }

      if (issues.length === 0) {
        this.showValidation(["File appears to be valid"], "success")
      } else {
        this.showValidation(issues, "warning")
      }
    } catch (error) {
      this.showValidation(["Invalid file format: " + error.message], "error")
    }
  }

  // Show validation results
  showValidation(messages, type) {
    const colorClasses = {
      success: "border-green-200 bg-green-50 text-green-700",
      warning: "border-yellow-200 bg-yellow-50 text-yellow-700",
      error: "border-red-200 bg-red-50 text-red-700"
    }

    if (this.hasValidationContentTarget) {
      this.validationContentTarget.innerHTML = messages
        .map((msg) => `<div class="text-sm ${colorClasses[type] || colorClasses.warning}">${msg}</div>`)
        .join("")
    }

    if (this.hasValidationTarget) {
      this.validationTarget.classList.remove("hidden")
    }
  }

  // Handle form submission
  handleFormSubmit(event) {
    if (!this.selectedFile) {
      event.preventDefault()
      alert("Please select a file to import")
      return
    }

    // Show progress
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("hidden")
    }

    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.textContent = "Importing..."
    }

    // Simulate progress
    let progress = 0
    const interval = setInterval(() => {
      progress += 10

      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = progress + "%"
      }

      if (this.hasProgressTextTarget) {
        this.progressTextTarget.textContent = progress + "%"
      }

      if (progress >= 90) {
        clearInterval(interval)
        if (this.hasProgressTextTarget) {
          this.progressTextTarget.textContent = "Processing..."
        }
      }
    }, 200)
  }
}
